import { motion, useScroll, useSpring, useTransform } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { InkButton } from '../components/InkButton';
import { InkedTitle } from '../components/InkedTitle';
import { Reveal } from '../components/Reveal';
import { useBooking } from '../context/BookingContext';
import { getArtistBySlug, getWorkById, styleLabels } from '../data';
import { useLang } from '../hooks/useLang';
import { NotFoundPage } from './NotFound';

/**
 * Long-form narrative page for a single work. Render-gated on `work.story`:
 * if the work has no diary, fall back to NotFound so we don’t ship empty pages.
 *
 * Layout signature:
 *  - Full-bleed hero with stamped metadata under the headline.
 *  - Chapters alternate sides (odd → image right, even → image left) so the
 *    eye zig-zags down the page instead of plodding in a single column.
 *  - A fixed mono progress indicator at top-right pins the chapter count.
 */
export function WorkDetailPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const params = useParams();
  const { openBooking } = useBooking();
  const work = params.id ? getWorkById(params.id) : undefined;

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 80, damping: 24, mass: 0.6 });
  const progressWidth = useTransform(progress, [0, 1], ['0%', '100%']);

  if (!work || !work.story) return <NotFoundPage />;

  const artist = getArtistBySlug(work.artistSlug);
  const styleLabel = styleLabels[work.style][lang];

  return (
    <>
      {/* Reading progress — a single hairline that fills as you read */}
      <div className="fixed left-0 right-0 top-0 z-30 h-px bg-ink-line/40" aria-hidden>
        <motion.div className="h-full bg-ink-accent origin-left" style={{ width: progressWidth }} />
      </div>

      {/* HERO — fullbleed photo, headline overlay */}
      <section className="relative h-[88svh] min-h-[560px] w-full overflow-hidden bg-ink-base">
        <img
          src={work.image}
          alt={work.alt[lang]}
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover ink-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/40 to-ink-base/20" />

        <div className="relative h-full mx-auto max-w-[1800px] px-6 md:px-10 flex flex-col">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="mt-28 md:mt-32 font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-paper/85 flex items-center gap-3"
          >
            <Link
              to="/lavori"
              className="hover:text-ink-paper inline-flex items-baseline gap-2 group"
            >
              <span aria-hidden className="transition-transform group-hover:-translate-x-0.5">
                ←
              </span>
              <span>{lang === 'it' ? 'Lavori' : 'Work'}</span>
            </Link>
            <span aria-hidden className="text-ink-paper/40">/</span>
            <span className="text-ink-paper/65">
              {styleLabel.toLowerCase()} · {work.year}
            </span>
          </motion.p>

          <div className="mt-auto pb-12 md:pb-20">
            <InkedTitle
              as="h1"
              immediate
              delay={0.15}
              duration={1.1}
              className="font-display tracking-display leading-[0.86] text-[clamp(2.75rem,9.5vw,9.5rem)] lowercase text-ink-paper"
            >
              {work.title[lang]}
            </InkedTitle>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
              className="mt-6 max-w-xl text-ink-paper/80 text-base md:text-lg leading-snug"
            >
              {work.description?.[lang] ?? work.alt[lang]}
            </motion.p>
          </div>
        </div>
      </section>

      {/* META BAR — editorial colophon */}
      <section className="relative border-y border-ink-line/50 bg-ink-base">
        <div className="mx-auto max-w-[1800px] px-6 md:px-10 py-6 md:py-7 grid grid-cols-2 md:grid-cols-6 gap-y-5 gap-x-6 md:gap-x-10 text-[11px] md:text-xs font-mono tracking-[0.18em] uppercase">
          <MetaCell label={t('common.artist')} value={artist?.name ?? '—'} />
          <MetaCell label={t('common.style')} value={styleLabel} />
          <MetaCell label={t('common.placement')} value={work.placement[lang]} />
          <MetaCell label={t('common.size')} value={work.size} />
          <MetaCell label={t('common.hours')} value={`${work.hours}h`} />
          <MetaCell label={t('common.year')} value={String(work.year)} />
        </div>
      </section>

      {/* CHAPTERS — alternating sides */}
      <section className="relative px-6 md:px-10 py-24 md:py-40">
        <div className="mx-auto max-w-[1800px] space-y-32 md:space-y-56">
          {work.story.map((ch, i) => {
            const imageRight = i % 2 === 0;
            return (
              <article
                key={ch.index}
                className="grid grid-cols-12 gap-x-6 gap-y-10 md:gap-x-10 md:gap-y-0 items-center"
              >
                {/* Text column */}
                <div
                  className={`col-span-12 md:col-span-5 ${
                    imageRight ? 'md:col-start-1' : 'md:col-start-8'
                  }`}
                >
                  <Reveal>
                    <p className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-muted flex items-center gap-3">
                      <span className="text-ink-accent">{ch.index}</span>
                      <span aria-hidden className="block h-px w-6 bg-ink-line" />
                      <span>{ch.stamp[lang]}</span>
                    </p>
                    <InkedTitle
                      as="h2"
                      className="mt-6 font-display tracking-display leading-[0.92] text-[clamp(1.875rem,4.5vw,4rem)] lowercase"
                    >
                      {ch.title[lang]}
                    </InkedTitle>
                    <p className="mt-8 max-w-md text-base md:text-lg text-ink-paper/80 leading-[1.55]">
                      {ch.body[lang]}
                    </p>
                  </Reveal>
                </div>

                {/* Image column */}
                {ch.image && (
                  <Reveal
                    className={`col-span-12 md:col-span-6 ${
                      imageRight ? 'md:col-start-7' : 'md:col-start-1 md:row-start-1'
                    }`}
                    delay={0.1}
                  >
                    <figure className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden bg-ink-line">
                      <img
                        src={ch.image}
                        alt={ch.title[lang]}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover ink-photo"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-base/40 via-transparent to-transparent" />
                      <figcaption className="absolute left-4 bottom-3 md:left-6 md:bottom-4 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-ink-paper/75">
                        {ch.stamp[lang]}
                      </figcaption>
                    </figure>
                  </Reveal>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* CLOSING — pull-quote + booking CTA */}
      <section className="relative border-t border-ink-line/50 bg-ink-paper text-ink-base px-6 md:px-10 py-28 md:py-40">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-base/55">
              {lang === 'it' ? 'Vuoi un pezzo così' : 'Want a piece like this'}
            </p>
            <InkedTitle
              as="h2"
              className="mt-8 font-display tracking-display leading-[0.92] text-[clamp(2.25rem,6.5vw,6.5rem)] lowercase text-ink-base"
            >
              {lang === 'it' ? `Prenota con ${artist?.name.split(' ')[0] ?? ''}` : `Book with ${artist?.name.split(' ')[0] ?? ''}`}
              <span className="text-ink-accent">.</span>
            </InkedTitle>
            <p className="mt-8 max-w-xl text-base md:text-lg text-ink-base/65">
              {lang === 'it'
                ? 'Risposta entro 48 ore lavorative. Consulenza gratuita. Caparra solo dopo l’approvazione del bozzetto.'
                : 'Reply within 48 working hours. Free consultation. Deposit only after sketch approval.'}
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5">
              <InkButton
                as="button"
                tone="dark"
                size="lg"
                onClick={() => openBooking({ artistSlug: artist?.slug, workId: work.id })}
              >
                {lang === 'it' ? 'Prenota una consulenza' : 'Book a consultation'}
              </InkButton>
              {artist && (
                <InkButton as="link" to={`/artisti/${artist.slug}`} variant="link" size="md" tone="dark">
                  {lang === 'it' ? `Tutto su ${artist.name.split(' ')[0]}` : `More on ${artist.name.split(' ')[0]}`}
                </InkButton>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-ink-muted">{label.toLowerCase()}</span>
      <span className="text-ink-paper">{value}</span>
    </div>
  );
}
